import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckCircle, FileText, Shield, XCircle } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { createAndApproveKyc, getAdminKycRequests } from '@/api/kyc';
import CreateKycRequestModal from '@/components/kyc/CreateKycRequestModal';

interface KycTabProps {
    customerId: string;
    customerName: string;
}

const KycTab: React.FC<KycTabProps> = ({ customerId, customerName }) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const [isCreateKycModalOpen, setIsCreateKycModalOpen] = useState(false);

    const {
        data: kycRequestsResponse,
        isLoading,
        refetch: refetchKycRequests,
    } = useQuery({
        queryKey: ['customerKycRequests', customerId],
        queryFn: () => getAdminKycRequests(customerId),
        enabled: !!customerId,
    });

    const kycRequests = kycRequestsResponse?.data || [];

    const getLatestKycRequest = () => {
        if (!kycRequests.length) return null;
        return [...kycRequests].sort(
            (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
        )[0];
    };

    const getKycStatusInfo = () => {
        const latestKyc = getLatestKycRequest();
        if (!latestKyc) {
            return {
                status: 'Not Submitted',
                submittedAt: null,
                reviewedAt: null,
                rejectionReason: null,
                adminOverride: false,
                documentsCount: 0,
            };
        }
        return {
            status: latestKyc.status,
            submittedAt: latestKyc.submitted_at,
            reviewedAt: latestKyc.reviewed_at,
            rejectionReason: latestKyc.rejection_reason,
            adminOverride: latestKyc.admin_override || false,
            documentsCount: latestKyc.documents.length,
        };
    };

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        KYC Information
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-6 w-28" />
                        </div>
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-36" />
                            <Skeleton className="h-5 w-16" />
                        </div>
                    </div>
                    <Skeleton className="h-16 w-full rounded-md" />
                    <div className="flex gap-2">
                        <Skeleton className="h-9 w-40" />
                        <Skeleton className="h-9 w-44" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    const kycInfo = getKycStatusInfo();

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        KYC Information
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">
                                Current Status
                            </label>
                            <div className="mt-1">
                                <StatusBadge status={kycInfo.status} kind="kyc" />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">
                                Documents Submitted
                            </label>
                            <div className="mt-1">
                                <span className="text-sm font-medium">
                                    {kycInfo.documentsCount} document
                                    {kycInfo.documentsCount !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {kycInfo.submittedAt && (
                            <div className="flex items-center gap-3 p-3 bg-info/10 border border-info/20 rounded-md">
                                <Calendar className="h-4 w-4 text-info" />
                                <div>
                                    <p className="text-sm font-medium text-info">
                                        Submitted on {format(new Date(kycInfo.submittedAt), 'PPP')}
                                    </p>
                                    <p className="text-xs text-info">
                                        {format(new Date(kycInfo.submittedAt), 'HH:mm')}
                                    </p>
                                </div>
                            </div>
                        )}
                        {kycInfo.reviewedAt && (
                            <div className="flex items-center gap-3 p-3 bg-success/10 border border-success/20 rounded-md">
                                <CheckCircle className="h-4 w-4 text-success" />
                                <div>
                                    <p className="text-sm font-medium text-success">
                                        Reviewed on {format(new Date(kycInfo.reviewedAt), 'PPP')}
                                    </p>
                                    <p className="text-xs text-success">
                                        {format(new Date(kycInfo.reviewedAt), 'HH:mm')}
                                    </p>
                                </div>
                            </div>
                        )}
                        {kycInfo.rejectionReason && (
                            <div className="flex items-center gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                                <XCircle className="h-4 w-4 text-destructive" />
                                <div>
                                    <p className="text-sm font-medium text-destructive">
                                        Rejection Reason
                                    </p>
                                    <p className="text-sm text-destructive">
                                        {kycInfo.rejectionReason}
                                    </p>
                                </div>
                            </div>
                        )}
                        {kycInfo.adminOverride && (
                            <div className="flex items-center gap-3 p-3 bg-warning/10 border border-warning/20 rounded-md">
                                <Shield className="h-4 w-4 text-warning" />
                                <div>
                                    <p className="text-sm font-medium text-warning">
                                        Admin Override Applied
                                    </p>
                                    <p className="text-xs text-warning">
                                        This decision was made using administrative override
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {kycRequests.length > 1 && (
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">
                                KYC History
                            </label>
                            <div className="mt-2 space-y-2">
                                {[...kycRequests]
                                    .sort(
                                        (a, b) =>
                                            new Date(b.submitted_at).getTime() -
                                            new Date(a.submitted_at).getTime()
                                    )
                                    .slice(0, 3)
                                    .map((request, index) => (
                                        <div
                                            key={request.id}
                                            className="flex items-center justify-between p-2 bg-muted rounded-md"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">
                                                    #{kycRequests.length - index}
                                                </span>
                                                <StatusBadge status={request.status} kind="kyc" />
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                                {format(
                                                    new Date(request.submitted_at),
                                                    'MMM dd, yyyy'
                                                )}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 pt-2 flex-wrap">
                        {hasPermission(ModulePermission.KYC, PermissionAction.CREATE) && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setIsCreateKycModalOpen(true)}
                            >
                                <Shield className="h-4 w-4" />
                                Create KYC Request
                            </Button>
                        )}
                        {hasPermission(ModulePermission.KYC, PermissionAction.EDIT) && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={async () => {
                                    try {
                                        await createAndApproveKyc(
                                            customerId,
                                            'Approved via one-click action',
                                            true
                                        );
                                        enqueueSnackbar('KYC created and approved successfully', {
                                            variant: 'success',
                                        });
                                        refetchKycRequests();
                                    } catch (e: any) {
                                        enqueueSnackbar(
                                            e?.response?.data?.message ||
                                                'Failed to create and approve KYC',
                                            { variant: 'error' }
                                        );
                                    }
                                }}
                            >
                                <CheckCircle className="h-4 w-4" />
                                One-click Approve KYC
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/kyc-requests?customer_id=${customerId}`)}
                        >
                            <FileText className="h-4 w-4" />
                            View KYC Requests
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/kyc-requests')}
                        >
                            <Shield className="h-4 w-4" />
                            Manage KYC
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <CreateKycRequestModal
                isOpen={isCreateKycModalOpen}
                onClose={() => setIsCreateKycModalOpen(false)}
                customerId={customerId}
                customerName={customerName}
                onSuccess={() => refetchKycRequests()}
            />
        </>
    );
};

export default KycTab;
